import { useContext, useMemo, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import { ImageContext } from "context";
import { IProps } from "../../types/types";
import IconFile from "../../assets/icons/file.svg";
import styles from "../../styles/FileField.module.scss";

export const FileField = ({ name, label, required, component }: IProps) => {
  const { filesContext, setFilesContext, validationTriggered } = useContext(ImageContext);
  const { register, trigger, setValue, formState: { submitCount } } = useFormContext();
  const [touched, setTouched] = useState(false);

  const hasFile = useMemo(() => {
    return filesContext[name] && filesContext[name].length > 0;
  }, [filesContext, name]);

  const showError = required && !hasFile && (touched || submitCount > 0 || validationTriggered);

  const fileName = useMemo(() => {
    if (!hasFile) return "";
    const _fileName: string = filesContext[name][0]["name"];
    return _fileName;
  }, [filesContext, name, hasFile]);

  useEffect(() => {
    if (hasFile) {
      setValue(name, filesContext[name]);
      trigger(name);
    } else {
      setValue(name, null);
    }
  }, [hasFile, filesContext, name, setValue, trigger]);

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files: FileList | any = event.target.files;
    setTouched(true);
    setFilesContext({
      ...filesContext,
      [name]: files,
    });
  };

  const onRemoveFile = () => {
    const newFilesContext = { ...filesContext };
    delete newFilesContext[name];
    setTouched(true);
    setFilesContext(newFilesContext);
  };

  const onReplaceFile = () => {
    const fileInput = document.getElementById(name) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <div className={styles.formItem}>
      <section style={{ display: "flex", flexDirection: "row", alignItems: "center", marginBottom: "0.5rem" }}>
        <span>{label}</span>
        {required && <span style={{ color: "red" }}> *</span>}
      </section>
      <div className={styles.wrapper}>
        {!hasFile ? (
          <>
            <div
              className={`${styles.containerFile} ${showError ? styles.containerFileError : ""}`}
              style={{ border: showError ? "2px solid #d32f2f" : "1px solid #6b6b6b" }}
            >
              <label htmlFor={name}>
                <img src={IconFile} alt="Icon upload file" />
                Subir archivo
              </label>
              <input
                type={component.type}
                id={name}
                accept=".png,.jpg,.jpeg,.pdf"
                {...register(name, { required: required })}
                onKeyUp={() => {
                  trigger(name);
                }}
                onChange={onFileChange}
                style={{ display: "none" }}
              />
            </div>
            {showError && (
              <span className={styles.errorMessage}>
                Este documento es obligatorio
              </span>
            )}
          </>
        ) : (
          <div className={styles.fileUploaded}>
            <div className={styles.fileInfo}>
              <img src={IconFile} alt="File icon" style={{ width: "20px", height: "20px" }} />
              <span className={styles.fileName}>{fileName}</span>
            </div>
            <div className={styles.fileActions}>
              <button
                type="button"
                onClick={onReplaceFile}
                className={styles.replaceButton}
                title="Reemplazar archivo"
              >
                Editar
              </button>
              <button
                type="button"
                onClick={onRemoveFile}
                className={styles.removeButton}
                title="Eliminar archivo"
              >
                Eliminar
              </button>
            </div>
            <input
              type={component.type}
              id={name}
              accept=".png,.jpg,.jpeg,.pdf"
              {...register(name, { required: required })}
              onChange={onFileChange}
              style={{ display: "none" }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
